/**
 * Wat te doen met een voorkomen dat de server weigerde.
 *
 * De regel die hier wordt afgedwongen: **een weigering mag nooit werk van de
 * modelleur weggooien.** Een weigering zegt iets over de boekhouding — jouw
 * teller loopt achter — en niet over de inhoud van wat je tekende.
 *
 * Twee gevallen, en het onderscheid zit in de vraag of er inhoudelijk iets is
 * veranderd sinds ons uitgangspunt:
 *
 * - **Alleen de teller liep achter.** De server heeft nog steeds wat wij als
 *   uitgangspunt hadden. Niemand heeft er echt aan gezeten; opnieuw aanbieden
 *   met de verse teller is veilig. Eén keer — blijft 't dan hangen, dan is er
 *   wél iets anders aan de hand en willen we niet in een lus twee sessies
 *   elkaar laten overschrijven.
 * - **Een collega wijzigde het voorkomen echt.** Niet overheen schrijven, maar
 *   ook niet weggooien: de versie van de modelleur blijft staan en 'ie hoort
 *   wat er speelt.
 */
import type { ViewElementState } from "@ae/domain";

export interface WeigeringsUitkomst {
  /** Opnieuw aanbieden met de verse teller — onze eigen boekhouding liep achter. */
  herkansen: string[];
  /** Een echte botsing: laten staan, melden, de modelleur beslist. */
  botsend: string[];
}

export function classificeerWeigering(
  sleutels: readonly string[],
  server: Record<string, ViewElementState | undefined>,
  uitgangspunt: Record<string, ViewElementState | undefined>,
  alGeprobeerd: (sleutel: string) => number,
  gelijk: (a: ViewElementState, b: ViewElementState) => boolean,
): WeigeringsUitkomst {
  const herkansen: string[] = [];
  const botsend: string[] = [];
  for (const sleutel of sleutels) {
    const vanServer = server[sleutel];
    const basis = uitgangspunt[sleutel];
    const mag = alGeprobeerd(sleutel) < 1;
    if (mag && vanServer && basis && gelijk(vanServer, basis)) herkansen.push(sleutel);
    else botsend.push(sleutel);
  }
  return { herkansen, botsend };
}

/**
 * Wat moet er mee in de patch: het verschil tussen wat de server heeft en wat
 * er lokaal staat.
 *
 * De valkuil zit niet in deze functie maar in wat je als `uitgangspunt`
 * meegeeft. Geef je de lokale stand — die op het moment van aanroepen al
 * bijgewerkt ís door de actie die de patch inplant — dan vergelijkt elke
 * wijziging zichzelf weg en stuur je niets. Een nieuw voorkomen ging wél mee
 * (die sleutel bestaat nog niet in het uitgangspunt), waardoor het beeld
 * ontstond dat tekenen werkt maar verplaatsen, verschalen, nesten en opmaak
 * "niet bewaard blijven".
 */
export function bepaalWatErHeenMoet(
  uitgangspunt: Record<string, ViewElementState>,
  lokaal: Record<string, ViewElementState>,
  gelijk: (a: ViewElementState, b: ViewElementState) => boolean,
): { gewijzigd: Record<string, ViewElementState>; verwijderd: string[] } {
  const gewijzigd: Record<string, ViewElementState> = {};
  for (const [sleutel, entry] of Object.entries(lokaal)) {
    const voor = uitgangspunt[sleutel];
    if (!voor || !gelijk(voor, entry)) gewijzigd[sleutel] = entry;
  }
  const verwijderd = Object.keys(uitgangspunt).filter((sleutel) => !(sleutel in lokaal));
  return { gewijzigd, verwijderd };
}
